import RouteEnum from '@/Enums/Routes/Route.enum';
import setAuthHeader, { getStoredAuthToken } from '@/helpers/set-auth-token';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectAuthToken } from '@/store/selectors/auth.selectors';
import { setToken } from '@/store/slices/authSlice';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const SSO_TOKEN_REGEX = /^[0-9a-f]{64}$/i;
const REDIRECT_SECONDS = 3;

enum Status {
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
  AlreadyAuthenticated = 'already_authenticated',
}

const SSO_ENDPOINT = '/sso/auth';

const SsoAuth = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const existingToken = useAppSelector(selectAuthToken);
  const initialTokenRef = useRef(existingToken || getStoredAuthToken());
  const [status, setStatus] = useState<Status>(initialTokenRef.current ? Status.AlreadyAuthenticated : Status.Loading);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (initialTokenRef.current) {
      setStatus(Status.AlreadyAuthenticated);
      return;
    }
    if (initialTokenRef.current) return;

    if (!token || !SSO_TOKEN_REGEX.test(token)) {
      setStatus(Status.Error);
      return;
    }

    axios
      .post(SSO_ENDPOINT, { token })
      .then(response => {
        const authToken = response.data.token;
        setAuthHeader(authToken);
        dispatch(setToken(authToken));
        setStatus(Status.Success);
      })
      .catch(() => {
        setStatus(Status.Error);
      });
  }, [token, dispatch]);

  useEffect(() => {
    if (status === Status.Loading) return;

    // '/' rather than a named page: the catch-all route resolves the landing route
    // from the user's screens, so SSO users on Unified Reporting are not dropped on
    // the legacy dashboard, which their nav no longer shows.
    const destination =
      status === Status.Success || status === Status.AlreadyAuthenticated
        ? '/'
        : `${RouteEnum.LOGIN}?error=sso_expired`;

    if (countdown === 0) {
      navigate(destination, { replace: true });
      return;
    }

    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  const renderContent = () => {
    switch (status) {
      case Status.Loading:
        return (
          <>
            <CircularProgress />
            <Typography color='text.secondary'>Authenticating…</Typography>
          </>
        );
      case Status.AlreadyAuthenticated:
        return (
          <>
            <Typography variant='h6'>You are already logged in.</Typography>
            <Typography color='text.secondary'>Redirecting in {countdown}…</Typography>
          </>
        );
      case Status.Success:
        return (
          <>
            <Typography variant='h6'>Authentication successful!</Typography>
            <Typography color='text.secondary'>Redirecting in {countdown}…</Typography>
          </>
        );
      case Status.Error:
        return (
          <>
            <Typography variant='h6' color='error'>
              This link is invalid or has expired.
            </Typography>
            <Typography color='text.secondary'>Redirecting to the login page in {countdown}…</Typography>
          </>
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: '100vh',
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default SsoAuth;
